import { TeacherBanners } from '@yandex-int/k-common/typings/bunker-nodes';
import b from 'b_';
import { Alarm as IconAlarm } from 'education-icons';
import { Link, Text, useStaticUrls } from 'platform-components';
import { TEXT_MARKUP_TYPE } from 'platform-components/constants';
import { getTeacherBanners } from 'platform-components/src/components/app-factory/selectors';
import { getIsTeacher } from 'platform-components/src/components/user/selectors';
import { useExactMatchParams } from 'platform-components/utils';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { getCurrentTeamLevel } from 'common.components/user/selectors';

import './banner.scss';

const BANNER_BACKGROUND = '#385797';

type BannerConfigItem = TeacherBanners['banners'][number];
type BannerProps = BannerConfigItem['banner'];

const Banner = React.memo(function Banner({
    warningMode,
    eventUrl,
    background,
    eventName,
    eventDescription,
    detailsButton,
}: BannerProps) {
    background = background || BANNER_BACKGROUND;
    return (
        <Link mix="banner" target="_blank" to={warningMode ? null : eventUrl}>
            <div className={b('banner__wrapper', { mode_warning: warningMode })} style={{ background }}>
                <div className="banner__content">
                    <div className="banner__event-name">
                        {warningMode && <IconAlarm className="banner__icon" height={24} width={24} />}
                        <Text type={TEXT_MARKUP_TYPE.MARKDOWN}>{eventName}</Text>
                    </div>
                    {!warningMode && eventDescription && (
                        <div className="banner__event-description">
                            <Text type={TEXT_MARKUP_TYPE.MARKDOWN}>{eventDescription}</Text>
                        </div>
                    )}
                </div>
                {!warningMode && (
                    <div className="banner__link">
                        <Text type={TEXT_MARKUP_TYPE.MARKDOWN}>{detailsButton}</Text>
                    </div>
                )}
            </div>
        </Link>
    );
});

const BannerFromTanker = () => {
    const intl = useIntl();
    const shouldShowBanner = intl.formatMessage({ id: 'banner.showKey' }) === 'true';
    if (!shouldShowBanner) {
        return null;
    }
    const eventName = intl.formatMessage({ id: 'banner.eventName' });
    const eventDescription = intl.formatMessage({ id: 'banner.eventDescription' }).trim();
    const detailsButton = intl.formatMessage({ id: 'banner.detailsButton' });
    const eventUrl = intl.formatMessage({ id: 'banner.eventUrl' });
    const background = intl.formatMessage({ id: 'banner.background', defaultMessage: BANNER_BACKGROUND });
    const warningMode = intl.formatMessage({ id: 'banner.warningMode' }) === 'true';

    return <Banner {...{ warningMode, eventUrl, background, eventName, eventDescription, detailsButton }} />;
};

const BannerContainer = React.memo(function BannerContainer(props: { grade: number | null; subject: string }) {
    const { grade, subject } = props;
    const { banners } = useSelector(getTeacherBanners) || {};
    if (!(grade && subject)) {
        return <BannerFromTanker />;
    }
    const bunkerBanner = banners?.find(({ grades, subjects }: BannerConfigItem) => {
        return grades?.includes(grade) && subjects?.includes(subject);
    });
    if (!bunkerBanner) {
        return <BannerFromTanker />;
    }
    if (bunkerBanner.showKey === false) {
        return null;
    }

    return <Banner {...bunkerBanner.banner} />;
});

const BannerContainerMemo = React.memo(function BannerContainerMemo() {
    const { classId, subject } = useExactMatchParams();
    const grade = useSelector((s) => getCurrentTeamLevel(s, { classId }));
    const { pathname } = useLocation();
    const { labClassSpecCourseStub } = useStaticUrls();
    const isSpecCourse = useMemo(() => {
        return matchPath(pathname, { path: labClassSpecCourseStub, exact: false });
    }, [labClassSpecCourseStub, pathname]);
    const isTeacher = useSelector(getIsTeacher);
    if (!isTeacher) {
        return null;
    }

    return <BannerContainer {...{ grade, subject: isSpecCourse ? 'spec-course' : subject }} />;
});

export default BannerContainerMemo;
